/**
 * Companion Navigation Maps — Spec 10-2
 * Route, section, and spotlight mappings for companion screen navigation assistance.
 */

import { COMPANION_NAME } from '@/lib/companion-config';
import { WILLIAMS_IDEA_POOL, daySeed, pickIdeas } from '@/lib/williams-ideas';
import type { StevensNavigationAction } from '@/types/chat';
import { msg, resolveMsg, type AnyTFunction, type Msg, type TKey } from '@/i18n/msg';

// ── Level 1: Route navigation ──────────────────────────────────────
//
// DORMANT. `getChipsForRoute` is the only export of this module with a live
// consumer (`StevensWidget.tsx`); NAVIGATION_MAP, SECTION_MAP,
// SPOTLIGHT_TARGETS and `executeNavigationAction` have none at HEAD, and the
// #641 lane-retirement plan re-checked that in its own build.
//
// Two things follow, and the second is the one that bites. The keys below are
// what a PARENT SAYS, so `medical` stays a key — the noun changed on the nav,
// not in anyone's mouth. The `description` values are what the companion says
// BACK, so they are labels and they follow the rename (`35601a57`: MedicalPage
// became HealthPage; the sidebar entry is now "Health").
//
// And: every treatment entry here — medications, symptoms, doctor questions —
// is an ungated route suggestion of exactly the shape the memorial gate exists
// to stop (#685/#693). It is safe today only because nothing calls it. Wiring
// any of it back up means giving it the same `isMemorial` gate
// `getChipsForRoute` carries below, in the same commit.
export const NAVIGATION_MAP: Record<string, { route: string; description: string }> = {
  appointments:       { route: '/calendar', description: 'your calendar' },
  calendar:           { route: '/calendar', description: 'your calendar' },
  medications:        { route: '/medical?tab=medications', description: 'your medications' },
  symptoms:           { route: '/medical?tab=symptoms', description: 'your symptom log' },
  'doctor questions': { route: '/medical?tab=questions', description: 'your questions' },
  health:             { route: '/medical', description: 'your health section' },
  medical:            { route: '/medical', description: 'your health section' },
  finances:           { route: '/bills', description: 'your finances' },
  tasks:              { route: '/tasks', description: 'your tasks' },
  circle:             { route: '/circle', description: 'your TRF contact and resources' },
  community:          { route: '/community', description: 'the community' },
  settings:           { route: '/settings', description: 'your settings' },
  home:               { route: '/dashboard', description: 'your home page' },
};

// ── Level 2: Section anchors ───────────────────────────────────────

export const SECTION_MAP: Record<string, { route: string; sectionId: string; description: string }> = {
  // The parent home's one anchor: the composer (guideline §2.9). The seven
  // dashboard anchors that sat under it (summary pills, shortcuts, upcoming
  // tasks / appointments, recent finances, support circle, recent activity)
  // pointed at blocks retired between #627 and 2026-09-18 and were pruned.
  'stevens hero':           { route: '/dashboard', sectionId: 'stevens-hero', description: `the ${COMPANION_NAME} composer` },
  'calendar view':          { route: '/calendar', sectionId: 'calendar-view', description: 'the calendar' },
  'appointments list':      { route: '/calendar', sectionId: 'appointments-list', description: 'your appointments' },
  'medications list':       { route: '/medical', sectionId: 'medications-list', description: 'your medications' },
  'questions list':         { route: '/medical', sectionId: 'questions-list', description: 'your questions' },
  'symptoms log':           { route: '/medical', sectionId: 'symptoms-log', description: 'your symptom log' },
  'task list':              { route: '/tasks', sectionId: 'task-list', description: 'your task list' },
  'member list':            { route: '/circle', sectionId: 'member-list', description: 'your circle members' },
  'uploaded documents':     { route: '/bills', sectionId: 'uploaded-documents', description: 'your uploaded receipts' },
  'upcoming expenses':      { route: '/bills', sectionId: 'upcoming-expenses', description: 'your upcoming expenses' },
};

// ── Level 3: Spotlight targets ─────────────────────────────────────

export const SPOTLIGHT_TARGETS = [
  'add-task',
  'add-appointment',
  'add-medication',
  'add-question',
  'log-symptom',
  'invite-member',
  // 'request-break' removed: the break-request wizard was deleted (#645)
  // and no data-spotlight="request-break" element exists anywhere.
  'stevens-input',
  // 'community-post' removed: families no longer post to Community (#2174),
  // so the composer it pointed at is gone.
] as const;

export type SpotlightTarget = typeof SPOTLIGHT_TARGETS[number];

// ── Page-aware contextual chips — Spec 10-1 Part 3 / Spec 11-1 Part 1.2 ────

/**
 * A route chip's label and the message it sends. A parent row is a pair of
 * `chat:widgetChips.<row>.{label,message}` descriptors (#2109 R3-B2: text sent
 * to the companion is written in the UI language); the widget resolves them
 * at render so a language switch re-renders the row. TRFer rows stay English
 * strings until R4.
 */
export type ChipText = string | Msg;
export interface RouteChip { label: ChipText; message: ChipText }

type ParentChipRow =
  | 'calendar.addAppointment' | 'calendar.thisWeek'
  | 'medical.logSymptom' | 'medical.doctorQuestions'
  | 'bills.explainCharge' | 'bills.trfHelp'
  | 'tasks.whatToAsk' | 'tasks.addRequest'
  | 'community.othersSaying'
  | 'help.tour' | 'help.mostUseful'
  | 'default.howCanIHelp' | 'default.focusToday';

function chip(row: ParentChipRow): RouteChip {
  return {
    label: msg(`chat:widgetChips.${row}.label` as TKey),
    message: msg(`chat:widgetChips.${row}.message` as TKey),
  };
}

/** A chip's text in the active language: a descriptor through `t`, a (staff) string as written. */
export function resolveChipText(t: AnyTFunction, text: ChipText): string {
  return typeof text === 'string' ? text : resolveMsg(t, text);
}

export const PAGE_CHIPS_CONFIG: Record<string, RouteChip[]> = {
  // Parent routes.
  // TREATMENT REGISTER. The /calendar and /medical rows below are the two that
  // must never reach a bereaved family — see MEMORIAL_PAGE_CHIPS_CONFIG.
  '/calendar':          [chip('calendar.addAppointment'), chip('calendar.thisWeek')],
  '/medical':           [chip('medical.logSymptom'), chip('medical.doctorQuestions')],
  '/bills':             [chip('bills.explainCharge'), chip('bills.trfHelp')],
  // /tasks chips (#645/#649 honesty pass): "Request a break" pointed at the
  // deleted break wizard, and "Remind my circle" implied notification
  // machinery that doesn't exist (the circle is a contact list — nobody is
  // notified). Offer what the page really does: add a help request to the
  // family task list, and talk through what to ask for.
  '/tasks':             [chip('tasks.whatToAsk'), chip('tasks.addRequest')],
  // Wave w1023c: /circle is Resources (the TRF contact and the resource
  // library). "Add someone" asked the companion to keep a contact it
  // cannot keep, and "Who's available" read a list that is gone.
  '/circle':            [chip('bills.trfHelp')],
  // #2174: Community is TRF announcements only: no "Post something" chip, and
  // the one chip asks what TRF posted, not what other parents say.
  '/community':         [chip('community.othersSaying')],
  '/settings':          [chip('default.howCanIHelp')],
  // TRFer routes. The home row is a draw from the same pool the Williams page
  // and the home hero draw from (2026-09-17, one carrier per fact —
  // `williams-ideas.ts`; three of twelve since 2026-09-18). This table is built
  // once per page load, so the day is the seed: the row rotates by the day.
  '/trfer':             pickIdeas(WILLIAMS_IDEA_POOL, daySeed()).map(({ label, message }) => ({ label, message })),
  // The Families page holds the Needs you view since 2026-09-14 (what
  // /trfer/queue was), so the queue prompts ride here with the roster ones.
  '/trfer/families':    [{ label: "Who hasn’t engaged this week?", message: "Which families haven’t engaged this week?" }, { label: "What’s most urgent?", message: "What’s the most urgent item in my queue?" }],
  // #626: was keyed '/trfer/circles' — a path that has been a redirect to
  // /trfer/resources since the rename, so these chips never rendered. Re-keyed
  // to the real path and re-worded for what the page is now (curated support
  // resources, not circles health).
  '/trfer/resources':   [{ label: 'Any coverage gaps?', message: 'Are there gaps in our resource coverage by country or treatment phase?' }, { label: 'What needs review?', message: 'Which curated resources look outdated or need review?' }],
  // #2174: no family-note queue any more; the page is TRF's announcements.
  '/trfer/community':   [{ label: 'Draft an announcement', message: 'Help me draft an announcement for families' }, { label: 'What did we post lately?', message: 'Summarize the announcements TRF posted recently' }],
  '/trfer/settings':    [{ label: 'How can I help?', message: 'How can I help?' }],
  // Help page
  '/help':              [chip('help.tour'), chip('help.mostUseful')],
};

/**
 * The memorial-register overrides (#685/#693) — the same two registers
 * `bereavement-tone.ts` keeps side by side, kept side by side here too so the
 * pair can be reviewed in one read rather than inferred from an absence.
 *
 * A chip is an OFFER. That is the whole defect: the treatment rows above are
 * correct for a family in treatment and are an invitation for a bereaved one,
 * and the invitation is the harm — "Log a symptom" offered to a parent whose
 * child has died, on a page (`HealthMemorialView`) that says in the same
 * viewport that the care record is closed. Two surfaces one scroll apart,
 * disagreeing about whether the child is alive.
 *
 * Route by route:
 *
 * - **`/medical` → no chips at all.** Every affordance the care hub has is a
 *   treatment write path, so there is no honest chip left to offer once the
 *   treatment ones are withheld; a substitute would be a chip invented to fill
 *   a row. `ChipBar` renders nothing for an empty array, which is the intended
 *   outcome and matches #644's suppression posture on the dashboard rail.
 * - **`/calendar` → the read chip survives, the write chip goes.** Unlike
 *   /medical, the calendar keeps a real remainder for a bereaved family
 *   (`CalendarPage` hides treatment events and keeps the rest), so suppressing
 *   the whole row would be over-correction. "Add appointment" is dropped:
 *   it drives `create_medical_appointment`. "What's this week look like?"
 *   stays, and is safe on the agent side too — Clio's `<bereavement_register>`
 *   answers a calendar question for a bereaved family from `get_tasks` only.
 *
 * Every other parent route's chips are register-neutral and need no override:
 * /tasks, /bills, /circle, /community, /settings and /help are all real,
 * ongoing business for a bereaved family (the #644 memo says so explicitly).
 * An empty array here means "suppress"; a missing key means "no change".
 */
export const MEMORIAL_PAGE_CHIPS_CONFIG: Record<string, RouteChip[]> = {
  '/medical': [],
  '/calendar': [chip('calendar.thisWeek')],
};

export const DEFAULT_CHIPS: RouteChip[] = [chip('default.howCanIHelp'), chip('default.focusToday')];

export interface ChipContext {
  /** TRFer family-detail chips only; ignored on every parent route. */
  familyName?: string;
  /**
   * `useDashboardMode().isMemorial`, OR-ed with that hook's `isLoading`.
   *
   * Required, not optional-with-a-default, and that is the point. This module
   * is a pure lookup table and cannot read the family record itself, so the
   * only gate available to it is one the caller has to supply — and a default
   * would let the next consumer of these chips reintroduce the defect by
   * saying nothing. Making it a compile error is the gate.
   *
   * Pass `false` outright on staff surfaces; the register does not apply
   * there and saying so explicitly is the whole benefit.
   */
  isMemorial: boolean;
}

export function getChipsForRoute(
  pathname: string,
  ctx: ChipContext,
): RouteChip[] {
  const { familyName, isMemorial } = ctx;
  // Strip any query string. Computed first because the memorial lookup needs
  // it too — /medical is reached as `/medical?tab=symptoms` from the chips
  // this very function used to hand out.
  const base = pathname.split('?')[0];

  // The memorial register wins over both the exact and the base match, so a
  // future PAGE_CHIPS_CONFIG row cannot outrank it by being more specific.
  if (isMemorial) {
    const override = MEMORIAL_PAGE_CHIPS_CONFIG[pathname] ?? MEMORIAL_PAGE_CHIPS_CONFIG[base];
    if (override) return override;
  }

  // Exact match first
  if (PAGE_CHIPS_CONFIG[pathname]) return PAGE_CHIPS_CONFIG[pathname];
  if (PAGE_CHIPS_CONFIG[base]) return PAGE_CHIPS_CONFIG[base];
  // TRFer family detail page — dynamic chips with family name
  if (base.startsWith('/trfer/families/')) {
    const name = familyName || 'this family';
    return [
      { label: `Prep me for the ${name} call`, message: `Prep me for the ${name} call` },
      { label: 'Draft notes from last call', message: `Draft notes from my last call with the ${name} family` },
    ];
  }
  // Parent dashboard and TRFer home use their own chips from the hero block
  if (base === '/dashboard' || base === '/' || base === '/trfer') return [];
  return DEFAULT_CHIPS;
}

// ── Execute navigation action ─────────────────────────────────────

const HEADER_OFFSET = 60;

export function scrollToSection(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
}

export function executeNavigationAction(
  action: StevensNavigationAction,
  navigate: (path: string) => void,
  currentPath: string,
) {
  const targetRoute = action.route.split('?')[0];
  const needsNavigation = currentPath.split('?')[0] !== targetRoute;

  if (action.type === 'navigate') {
    if (needsNavigation) navigate(action.route);
    return;
  }

  if (action.type === 'scroll_to') {
    if (needsNavigation) {
      navigate(action.route);
      // Wait for page render, then scroll
      setTimeout(() => {
        if (action.sectionId) scrollToSection(action.sectionId);
      }, 300);
    } else if (action.sectionId) {
      scrollToSection(action.sectionId);
    }
    return;
  }

  if (action.type === 'spotlight') {
    const doSpotlight = () => {
      if (action.sectionId) scrollToSection(action.sectionId);
      if (action.spotlightTarget) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('stevens:spotlight', {
            detail: { target: action.spotlightTarget },
          }));
        }, 400);
      }
    };

    if (needsNavigation) {
      navigate(action.route);
      setTimeout(doSpotlight, 500);
    } else {
      doSpotlight();
    }
  }
}
